/**
 * 导演交代卡(W4 批,2026-09-13,设计稿拍板)—— 替换幕二原「故事大纲」折叠卡的位置。
 *
 * ═══ 它比旧「故事大纲」多说了什么 ═══════════════════════════════════════════════
 * 旧卡只有 logline/主角/beats,是架构师一层的产物;新导演管线还产了「选了哪些卖点/
 * 为什么、这条片什么结构、审片人怎么说」——此前这些字段进了 formJson 却零渲染,
 * 用户看不出「这次编排替我做了哪些决定」。这张卡把它们摊开,并给「选错了」一个改口
 * (换进去 → 钉住重编排)。
 *
 * ★老草稿 / 旧 ai 响应都没有这些新字段:除旧「故事大纲」内容(收进本卡「完整大纲」折叠)
 * 外,新增的每个子块各自按自己的字段缺席判断要不要显示;整卡按「有没有任何可说的」判断
 * 要不要存在 —— 都没有就等于旧「故事大纲」也不该显示那次,原样不渲染。
 */
import { useState } from "react";
import { ChevronDown, Film } from "lucide-react";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/shared/ui/collapsible";
import { cn } from "@/shared/lib/utils";
import type { FormState } from "../../model/videoCreateTypes";
import { ARGUMENT_ARC_TINT, argumentArcRuns } from "../../model/argumentArc";
import { architectureLabel, factText, swappedFactSet } from "../../model/directorBrief";
import { briefShownDurationSec } from "../../model/effectiveDuration";
import { textOverlayOf, textOverlayText, toneRegisterLabel } from "../../model/toneRegister";
import { VideoFactSwapDialog } from "./VideoFactSwapDialog";

interface Props {
  form: FormState;
  /** 「预计成片 Xs · N 镜」的镜数(=当前分镜数,页面已经算好,本组件不重算)。 */
  shotCount: number;
  /** 编排/出图进行中:换料按钮禁用(服务端正在写,这时候再发一轮是 dual-write)。 */
  disabled?: boolean;
  /** 「换进去」确认后触发 —— 页面负责真正走重编排那条路(guardedCompose)。 */
  onRecomposeWithPins: (pinnedFactIds: string[]) => void;
  /** 「恢复自动选料」——只清 pinnedFactIds,不自动重编排。 */
  onClearPinnedFacts: () => void;
}

export function VideoDirectorBriefCard({
  form,
  shotCount,
  disabled,
  onRecomposeWithPins,
  onClearPinnedFacts,
}: Props) {
  const [swapTarget, setSwapTarget] = useState<string | null>(null);

  const storyBible = form.storyBible ?? null;
  const hasOutline = !!(storyBible?.logline || (storyBible?.beats?.length ?? 0) > 0);
  const selected = form.selectedFacts ?? [];
  const unused = form.unusedFacts ?? [];
  const factTexts = form.factTexts ?? null;
  const hasFacts = selected.length > 0 || unused.length > 0;
  const arcRuns = argumentArcRuns(form.narrativeRoles);
  const archLabel = architectureLabel(storyBible?.architecture_type);
  const hasReviewVerdict = !!form.reviewVerdict?.trim();
  // 1013 V5:审片人缺料反问(只有用户知道的事实)—— 此前进了 formJson 却零渲染,页面从不反问。
  const missingInputs = (form.reviewMissingInputs ?? []).filter((m) => !!m?.q?.trim());
  const hasPinned = (form.pinnedFactIds?.length ?? 0) > 0;
  // 0926:已编排多镜时显示 Σ各镜时长(真会渲染/计费的秒数),不显示导演编排前的估计 —— 见 briefShownDurationSec。
  const shownSec = briefShownDurationSec(form);

  // 0924 车 C:本轮按哪个调性档编 + 导演判的画面大字档(只读,载体 hookPlan.textOverlay)。
  const overlayText = textOverlayText(textOverlayOf(form.hookPlan, form.textOverlay));
  const registerText = toneRegisterLabel(form.toneRegister);
  const hasAnything =
    !!overlayText ||
    hasOutline ||
    hasFacts ||
    arcRuns.length > 0 ||
    shownSec != null ||
    form.reviewScore != null ||
    hasReviewVerdict ||
    missingInputs.length > 0 ||
    !!archLabel;
  if (!hasAnything) return null;

  return (
    <div data-video-director-brief className="rounded-lg border border-border/60 bg-card/40 p-3">
      {/* 头行:标题 + 架构 chip + 预计成片 + 审片 chip */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="flex items-center gap-1.5 text-sm font-medium">
          <Film className="h-3.5 w-3.5 text-primary" />
          导演的编排
        </span>
        {archLabel && (
          <span className="rounded-full border border-primary/30 bg-primary/5 px-2 py-0.5 text-2xs text-primary">
            {archLabel}
          </span>
        )}
        {shownSec != null && (
          <span className="text-2xs text-muted-foreground">
            预计成片 {shownSec}s · {shotCount} 镜
          </span>
        )}
        {overlayText && (
          <span
            data-testid="director-brief-text-overlay"
            className="text-2xs text-muted-foreground"
          >
            {registerText ? `调性 ${registerText} · ` : ""}大字:{overlayText}
          </span>
        )}
        {form.reviewScore != null && (
          <span
            data-testid="director-brief-review-score"
            className="ml-auto rounded-full border border-emerald-300/50 bg-emerald-50 px-2 py-0.5 text-2xs text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-950/30 dark:text-emerald-300"
          >
            审片 {form.reviewScore} 分
          </span>
        )}
      </div>

      {/* 1013 V5:缺料反问 —— 导演不编这些事实,用户补进文案后重新编排 */}
      {missingInputs.length > 0 && (
        <div
          data-testid="director-brief-missing-inputs"
          className="mt-2 rounded-md border border-amber-300/60 bg-amber-50 p-2 text-2xs text-amber-800 dark:border-amber-400/30 dark:bg-amber-950/30 dark:text-amber-200"
        >
          <p className="font-medium">审片人想先问你几件事(导演不会替你编,补进文案后重新编排):</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-4">
            {missingInputs.map((m, i) => (
              <li key={i}>
                {m.q}
                {m.why?.trim() ? (
                  <span className="text-amber-700/80 dark:text-amber-300/70">({m.why})</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 立意 */}
      {form.theme && <p className="mt-1.5 text-xs text-foreground/85">{form.theme}</p>}

      {/* 选料清单 */}
      {hasFacts && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {selected.map((fid) => (
            <span
              key={fid}
              className="rounded-full border border-emerald-300/50 bg-emerald-50 px-2 py-0.5 text-2xs text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-950/30 dark:text-emerald-300"
            >
              {factText(fid, factTexts)}
            </span>
          ))}
          {unused.length > 0 && (
            <span className="mx-0.5 text-2xs text-muted-foreground">
              这 {unused.length} 条这次不讲
            </span>
          )}
          {unused.map((fid) => (
            <span
              key={fid}
              className="inline-flex items-center gap-1 rounded-full border border-dashed border-border/60 px-2 py-0.5 text-2xs text-muted-foreground"
            >
              {factText(fid, factTexts)}
              <button
                type="button"
                disabled={disabled}
                data-testid={`fact-swap-in-${fid}`}
                onClick={() => setSwapTarget(fid)}
                className="cursor-pointer text-primary underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
              >
                换进去
              </button>
            </span>
          ))}
        </div>
      )}

      {/* 钉选料提示行 */}
      {hasPinned && (
        <p className="mt-1.5 text-2xs text-muted-foreground">
          已按你钉的选料编排 ·{" "}
          <button
            type="button"
            data-testid="director-brief-clear-pin"
            onClick={onClearPinnedFacts}
            className="cursor-pointer text-primary underline-offset-2 hover:underline"
          >
            恢复自动选料
          </button>
        </p>
      )}

      {/* 论证弧 pills */}
      {arcRuns.length > 0 && (
        <div
          data-testid="director-brief-argument-arc"
          className="mt-2 flex flex-wrap items-center gap-x-1 gap-y-1"
        >
          {arcRuns.map((r, i) => (
            <span key={i} className="flex items-center gap-1">
              {i > 0 && <span className="text-2xs text-muted-foreground">→</span>}
              <span
                className={cn(
                  "rounded-full border px-2 py-0.5 text-2xs font-medium",
                  ARGUMENT_ARC_TINT[r.role] ?? "border-border/50 text-muted-foreground"
                )}
              >
                {r.label} ×{r.count}
              </span>
            </span>
          ))}
        </div>
      )}

      {/* 折叠区:旧故事大纲 + 审片人意见 */}
      {(hasOutline || hasReviewVerdict) && (
        <Collapsible>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="mt-2 flex w-full cursor-pointer items-center justify-between text-left text-2xs text-muted-foreground hover:text-foreground"
            >
              完整大纲
              <ChevronDown className="h-3.5 w-3.5 shrink-0" />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-1.5 space-y-1 border-t border-border/50 pt-1.5 text-2xs text-muted-foreground">
            {storyBible?.protagonist?.name && (
              <div>
                <span className="text-primary">主角:</span> {storyBible.protagonist.name}
                {storyBible.protagonist.appearance
                  ? `（${storyBible.protagonist.appearance}）`
                  : ""}
              </div>
            )}
            {(storyBible?.beats ?? []).map((bt, i) => (
              <div key={i}>
                {i + 1}. {bt}
              </div>
            ))}
            {hasReviewVerdict && (
              <p className="pt-1 text-foreground/80">审片人意见:{form.reviewVerdict}</p>
            )}
          </CollapsibleContent>
        </Collapsible>
      )}

      <VideoFactSwapDialog
        open={swapTarget != null}
        onOpenChange={(o) => !o && setSwapTarget(null)}
        incomingFid={swapTarget}
        selected={selected}
        factTexts={factTexts}
        estimatedDurationSec={shownSec}
        onConfirm={(releasedFid) => {
          if (!swapTarget) return;
          onRecomposeWithPins(swappedFactSet(selected, swapTarget, releasedFid));
          setSwapTarget(null);
        }}
      />
    </div>
  );
}
